import {
	createContext,
	useState,
	useEffect,
	useCallback,
	useMemo,
	PropsWithChildren,
	useRef,
	RefObject,
} from 'react';
import { Util } from '../util/Util';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../api/axios';
import { syncBusinessClock } from '../util/businessClock';
import { API_BASE_URL } from '../util/constants';
import { useTranslation } from 'react-i18next';
import { useAppSocket } from './AppSocketContext';
import {
	clearStoredSession,
	getSessionAccessToken,
	isRefreshLockedByOtherTab,
	isSessionNewer,
	readStoredSession,
	releaseRefreshLock,
	sameAccessToken,
	subscribeSessionSync,
	tryAcquireRefreshLock,
	waitForPeerSessionRefresh,
	writeStoredSession,
} from '../util/sessionStore';
import {
	canAccessPath,
	getDefaultPathForAccess,
} from '../util/routeAccess';
import { announceSessionExpired } from '../util/sessionExpiry';
import {
	AccessPolicy,
	EMPTY_ACCESS,
	accessFingerprint,
	normalizeAccess,
} from '../util/access';

interface Props {
	session: any | null;
	/** Monotonic counter bumped whenever live access grants change. */
	accessVersion: number;
	/** Column and field rules: what this user may see and set within a screen. */
	access: AccessPolicy;
	/** Apply a full session object (login / check_session). Persists + broadcasts by default. */
	setSession: (session: any | null) => void;
	/** Server-side revoke + clear all tabs. */
	logout: () => Promise<void>;
	/**
	 * Validate session with the API.
	 * @param serverCheck hit the server (default true)
	 * @param options.slide when true, rotate JWT and blocklist the previous one (keep-alive only)
	 */
	checkSession: (
		serverCheck?: boolean,
		options?: { slide?: boolean }
	) => Promise<void> | void;
	/** Stable keep-alive entry: slides JWT only when near expiry (single-flight). */
	requestSessionKeepAlive: () => Promise<void>;
	sessionRef: RefObject<any>;
	api: typeof api;
}

const AuthContext = createContext<Props | undefined>(undefined);

/** Access poll via socket when connected (REST is the reliability fallback). */
const PERMISSIONS_SOCKET_POLL_MS = 30000;

const canonicalSession = (raw: any) => {
	if (!raw || typeof raw !== 'object') return raw;
	const session = { ...raw };
	delete session.permissions;
	delete session.operations;
	const rawAccess = raw.access ?? raw;
	const access = normalizeAccess(rawAccess.resources, rawAccess.postings);
	return {
		...session,
		access,
	};
};

const AuthProvider = ({ children }: PropsWithChildren) => {
	// Synchronous hydrate so the first paint already has a Bearer token available
	// for module-level axios interceptors (avoids 401 race on hard refresh).
	const [session, setSessionState] = useState<any | null>(() => {
		const stored = readStoredSession();
		if (stored && Util.isAccessTokenUsable(stored.access_token)) {
			return canonicalSession(stored);
		}
		return null;
	});
	const [accessVersion, setAccessVersion] = useState(0);

	const { i18n } = useTranslation();
	const { socket, connected } = useAppSocket();

	const sessionRef = useRef<any>(null);
	const bootstrappedRef = useRef(false);
	/** Avoid infinite check_session loop: each success issues a new JWT (new access_token). */
	const didInitialServerCheckRef = useRef(false);
	const lastAccessFingerprintRef = useRef<string>('');
	/** Suppress treating our own writes as remote when storage+channel both fire. */
	const applyingRemoteRef = useRef(false);
	/** In-tab single-flight for /check_session. */
	const checkSessionInFlightRef = useRef<Promise<void> | null>(null);

	const navigate = useNavigate();
	const location = useLocation();
	const locationPathRef = useRef(location.pathname);
	locationPathRef.current = location.pathname;

	// Keep ref in lockstep with state during render so async handlers always see latest session.
	sessionRef.current = session;

	const bumpAccessIfNeeded = useCallback((access?: AccessPolicy) => {
		const fp = accessFingerprint(access);
		if (fp && fp !== lastAccessFingerprintRef.current) {
			lastAccessFingerprintRef.current = fp;
			setAccessVersion((v) => v + 1);
		}
		if (!fp) {
			lastAccessFingerprintRef.current = '';
		}
	}, []);

	/**
	 * Single write path for session identity across this tab and others.
	 * - persist: write/clear localStorage
	 * - broadcast: notify other tabs (via sessionStore)
	 */
	const commitSession = useCallback(
		(
			next: any | null,
			options: {
				persist?: boolean;
				broadcast?: boolean;
				reason?: string;
			} = {}
		) => {
			const { persist = true, broadcast = true } = options;

			if (next && getSessionAccessToken(next)) {
				next = canonicalSession(next);
				if (persist) {
					writeStoredSession(next, { broadcast });
				}
				setSessionState((prev: any) => {
					if (sameAccessToken(prev, next) && accessFingerprint(prev?.access) === accessFingerprint(next.access)) {
						// Still replace if other profile fields changed (lang, roles, …).
						const prevJson = JSON.stringify(prev);
						const nextJson = JSON.stringify(next);
						if (prevJson === nextJson) return prev;
					}
					return next;
				});
				bumpAccessIfNeeded(next.access);
				if (next.lang) {
					try {
						i18n.changeLanguage(next.lang);
					} catch {
						/* ignore */
					}
				}
			} else {
				if (persist) {
					clearStoredSession({ broadcast });
				}
				setSessionState(null);
				lastAccessFingerprintRef.current = '';
				setAccessVersion((v) => v + 1);
			}
		},
		[bumpAccessIfNeeded, i18n]
	);

	/** Public setter used by login and callers — always persists + broadcasts. */
	const setSession = useCallback(
		(next: any | null) => {
			commitSession(next, { persist: true, broadcast: true, reason: 'setSession' });
		},
		[commitSession]
	);

	const getAccessToken = useCallback((): string | null => {
		return (
			getSessionAccessToken(sessionRef.current) ||
			getSessionAccessToken(readStoredSession())
		);
	}, []);

	const applyAccess = useCallback(
		(rawResources?: unknown, rawPostings?: unknown) => {
			const current = sessionRef.current;
			if (!current?.access_token) return;
			const access = normalizeAccess(rawResources, rawPostings);
			const sameAccess = accessFingerprint(current.access) === accessFingerprint(access);
			if (sameAccess) return;

			const updated = { ...current, access };
			// Persist + broadcast so every tab applies the same ACL.
			commitSession(updated, {
				persist: true,
				broadcast: true,
				reason: 'permissions',
			});
		},
		[commitSession]
	);

	const requestPermissionsViaSocket = useCallback(() => {
		if (!socket?.connected) return;
		const token = getAccessToken();
		if (!token || !Util.isAccessTokenUsable(token)) return;
		socket.emit('get_user_permissions', { token });
	}, [socket, getAccessToken]);

	const requestPermissionsViaRest = useCallback(async () => {
		const token = getAccessToken();
		if (!token || !Util.isAccessTokenUsable(token)) return;

		try {
			const res = await api.get('/users/permissions');
			if (res?.status === 200 && res.data?.resources && res.data?.postings) {
				applyAccess(res.data.resources, res.data.postings);
			}
		} catch (err) {
			console.warn('REST permissions refresh failed', err);
		}
	}, [getAccessToken, applyAccess]);

	const checkSession = useCallback(
		async (serverCheck: boolean = true, options: { slide?: boolean } = {}) => {
			const slide = Boolean(options.slide);

			// Coalesce concurrent *slide* keep-alives in this tab.
			// Validate-only calls are cheap and must not block page data loads.
			if (serverCheck && slide && checkSessionInFlightRef.current) {
				return checkSessionInFlightRef.current;
			}

			const run = async () => {
				const current = sessionRef.current || readStoredSession();

				// Sliding may run in the last seconds of life — only require not-yet-expired (1s skew).
				// Validate-only keeps the stricter default skew for normal checks.
				const usableSkewMs = slide ? 1_000 : 5_000;
				if (!current || !Util.isAccessTokenUsable(current.access_token, usableSkewMs)) {
					if (current && !slide) {
						// Expired local token — clear harmony across tabs.
						commitSession(null, { persist: true, broadcast: true, reason: 'expired' });
					} else if (current && slide) {
						// Slide attempted too late — treat as expired.
						commitSession(null, { persist: true, broadcast: true, reason: 'expired-slide' });
					}
					// Stay on the current page (POS especially) and show the re-auth
					// modal. Navigating to /login unmounted the terminal and raced the
					// modal, which is what flickered the screen.
					if (locationPathRef.current === '/login') {
						return;
					}
					if (current) {
						announceSessionExpired();
					} else {
						navigate('/login');
					}
					return;
				}

				// Hydrate React state from storage if needed.
				if (!sessionRef.current) {
					commitSession(current, { persist: false, broadcast: false, reason: 'hydrate' });
				}

				if (!serverCheck) return;

				const token = current.access_token as string;

				// Multi-tab lock only when rotating — concurrent pages must not race-revoke.
				if (slide) {
					if (isRefreshLockedByOtherTab() || !tryAcquireRefreshLock(token)) {
						const peer = await waitForPeerSessionRefresh(token);
						if (peer?.access_token && Util.isAccessTokenUsable(peer.access_token, 1_000)) {
							if (!sameAccessToken(sessionRef.current, peer)) {
								commitSession(peer, {
									persist: false,
									broadcast: false,
									reason: 'peer-refresh',
								});
							}
							return;
						}
						if (!tryAcquireRefreshLock(token)) {
							return;
						}
					}
				}

				try {
					// Interceptor suppresses re-auth modal for check_session URL.
					const res = await api.get('/check_session', {
						params: slide ? { slide: 1 } : undefined,
					});
					if (res?.status === 200 && res.data) {
						const lifetime =
							typeof res.data.expiry === 'number' && res.data.expiry >= 30
								? res.data.expiry
								: typeof current.sessionLifetime === 'number'
									? current.sessionLifetime
									: typeof current.expiry === 'number' && current.expiry >= 30
										? current.expiry
										: undefined;

						// slide=1 → new token (old jti blocklisted). validate-only → same token + fresh perms.
						commitSession(
							{
								...current,
								...res.data,
								// Prefer server token; fall back to existing if omitted.
								access_token: res.data.access_token || token,
								// Always keep configured lifetime for keep-alive math.
								...(lifetime != null
									? { expiry: lifetime, sessionLifetime: lifetime }
									: {}),
							},
							{
								persist: true,
								broadcast: true,
								reason: slide ? 'check_session-slide' : 'check_session',
							}
						);
						return;
					}

					// 401/422: another tab may have rotated — adopt storage if newer.
					const stored = readStoredSession();
					if (
						stored?.access_token &&
						stored.access_token !== token &&
						Util.isAccessTokenUsable(stored.access_token)
					) {
						commitSession(stored, {
							persist: false,
							broadcast: false,
							reason: 'check_session-stale-401',
						});
					}
					// A 401 here is often a raced slide, not a dead session. The
					// interceptor already skipped the re-auth modal for this URL;
					// do not re-introduce it — opening POS was covering the till.
				} catch (err) {
					const stored = readStoredSession();
					if (
						stored?.access_token &&
						stored.access_token !== token &&
						Util.isAccessTokenUsable(stored.access_token)
					) {
						commitSession(stored, {
							persist: false,
							broadcast: false,
							reason: 'check_session-error-hydrate',
						});
					} else {
						console.warn('check_session failed', err);
					}
				} finally {
					if (slide) {
						releaseRefreshLock();
					}
				}
			};

			if (!serverCheck || !slide) {
				return run();
			}

			const promise = run().finally(() => {
				if (checkSessionInFlightRef.current === promise) {
					checkSessionInFlightRef.current = null;
				}
			});
			checkSessionInFlightRef.current = promise;
			return promise;
		},
		[commitSession, navigate]
	);

	/** Keep-alive: near-expiry JWT slide (server blocklists previous jti). */
	const requestSessionKeepAlive = useCallback(async () => {
		await checkSession(true, { slide: true });
	}, [checkSession]);

	const logout = useCallback(async () => {
		const token = getAccessToken();
		try {
			if (token && Util.isAccessTokenUsable(token)) {
				// Best-effort server revoke; client always clears regardless.
				await api.post('/logout');
			}
		} catch (err) {
			console.warn('Logout revoke failed (clearing client anyway)', err);
		} finally {
			commitSession(null, { persist: true, broadcast: true, reason: 'logout' });
			navigate('/login');
		}
	}, [getAccessToken, commitSession, navigate]);

	// Business clock: dates follow the server's timezone and clock, not the browser's.
	useEffect(() => {
		void syncBusinessClock(API_BASE_URL);
		const timer = window.setInterval(() => void syncBusinessClock(API_BASE_URL), 60 * 60 * 1000);
		const onVisible = () => {
			if (document.visibilityState === 'visible') void syncBusinessClock(API_BASE_URL);
		};
		document.addEventListener('visibilitychange', onVisible);
		return () => {
			window.clearInterval(timer);
			document.removeEventListener('visibilitychange', onVisible);
		};
	}, []);

	// Bootstrap once: session may already be hydrated from storage in useState.
	useEffect(() => {
		if (bootstrappedRef.current) return;
		bootstrappedRef.current = true;

		const current = sessionRef.current || readStoredSession();
		if (current && Util.isAccessTokenUsable(current.access_token)) {
			lastAccessFingerprintRef.current = accessFingerprint(current.access);
			if (!sessionRef.current) {
				setSessionState(canonicalSession(current));
			}
			return;
		}

		if (current) {
			// Stale/expired — drop so tabs stay consistent.
			clearStoredSession({ broadcast: true });
			setSessionState(null);
		}

		if (location.pathname !== '/login') {
			navigate('/login');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Cross-tab session harmony.
	useEffect(() => {
		return subscribeSessionSync((message) => {
			if (applyingRemoteRef.current) return;

			// Refresh coordination messages are handled via waitForPeerSessionRefresh / locks.
			if (
				message.type === 'SESSION_REFRESH_START' ||
				message.type === 'SESSION_REFRESH_DONE'
			) {
				return;
			}

			if (message.type === 'SESSION_CLEAR') {
				applyingRemoteRef.current = true;
				try {
					setSessionState(null);
					lastAccessFingerprintRef.current = '';
					setAccessVersion((v) => v + 1);
					if (location.pathname !== '/login') {
						navigate('/login');
					}
				} finally {
					applyingRemoteRef.current = false;
				}
				return;
			}

			if (message.type !== 'SESSION_SET') return;

			const incoming = message.session;
			if (!incoming?.access_token) return;
			if (!Util.isAccessTokenUsable(incoming.access_token)) return;

			const canonicalIncoming = canonicalSession(incoming);
			const current = sessionRef.current;
			if (current && !isSessionNewer(canonicalIncoming, current) && sameAccessToken(current, canonicalIncoming)) {
				if (accessFingerprint(current.access) === accessFingerprint(canonicalIncoming.access)) return;
			}

			applyingRemoteRef.current = true;
			try {
				// Already in localStorage from the other tab — do not re-write/broadcast.
				setSessionState(canonicalIncoming);
				bumpAccessIfNeeded(canonicalIncoming.access);
				if (canonicalIncoming.lang) {
					try {
						i18n.changeLanguage(canonicalIncoming.lang);
					} catch {
						/* ignore */
					}
				}
				// Leave /login if we just received a live session.
				if (location.pathname === '/login') {
					navigate(getDefaultPathForAccess({
						access: canonicalIncoming.access,
					}), { replace: true });
				}
			} finally {
				applyingRemoteRef.current = false;
			}
		});
	}, [navigate, location.pathname, bumpAccessIfNeeded, i18n]);

	// Soft server validation once after login/restore (not on every keep-alive rotation).
	// Key by user_id so token slides don't re-trigger; reset on logout / user switch.
	useEffect(() => {
		if (!session?.access_token || !Util.isAccessTokenUsable(session.access_token)) {
			didInitialServerCheckRef.current = false;
			return;
		}
		if (didInitialServerCheckRef.current) return;
		didInitialServerCheckRef.current = true;
		void checkSession(true);
	}, [session?.user_id, checkSession]);

	// When user identity or ACL changes, leave pages the new principal cannot open.
	useEffect(() => {
		if (!session?.access_token) return;
		if (!Util.isAccessTokenUsable(session.access_token)) return;
		const ctx = { access: session.access };
		if (location.pathname === '/login') {
			navigate(getDefaultPathForAccess(ctx), { replace: true });
			return;
		}

		if (!canAccessPath(location.pathname, ctx)) {
			navigate(getDefaultPathForAccess(ctx), { replace: true });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [session?.user_id, accessVersion, location.pathname]);

	// Socket ack: soft-fail on error; never clear existing grants.
	useEffect(() => {
		if (!socket) return;

		const onPermissionsAck = (data: any) => {
			try {
				if (data?.status === 'success') {
					applyAccess(data.resources, data.postings);
				} else {
					console.warn(
						'Socket permissions refresh failed:',
						data?.message || 'unknown error'
					);
				}
			} catch (err) {
				console.error('Failed to apply socket user permissions', err);
			}
		};

		socket.on('get_user_permissions_ack', onPermissionsAck);
		return () => {
			socket.off('get_user_permissions_ack', onPermissionsAck);
		};
	}, [socket, applyAccess]);

	// Immediate socket poll on connect + while session has a token; then interval.
	useEffect(() => {
		if (!connected || !socket) return;
		if (!getAccessToken()) return;

		requestPermissionsViaSocket();
		const interval = setInterval(
			requestPermissionsViaSocket,
			PERMISSIONS_SOCKET_POLL_MS
		);
		return () => clearInterval(interval);
	}, [
		connected,
		socket,
		session?.access_token,
		getAccessToken,
		requestPermissionsViaSocket,
	]);

	// REST fallback when a session token becomes available (login / restore / cross-tab).
	useEffect(() => {
		if (!session?.access_token || !Util.isAccessTokenUsable(session.access_token)) return;
		void requestPermissionsViaRest();
	}, [session?.access_token, session?.user_id, requestPermissionsViaRest]);

	// Refresh grants when the tab regains focus (socket + REST) and re-hydrate from storage.
	useEffect(() => {
		const onFocus = () => {
			const stored = readStoredSession();
			const current = sessionRef.current;

			if (!stored || !Util.isAccessTokenUsable(stored.access_token)) {
				if (current) {
					commitSession(null, { persist: true, broadcast: true, reason: 'focus-expired' });
					if (location.pathname !== '/login') {
						announceSessionExpired();
					}
				}
				return;
			}

			// Another tab may have logged a different user in while we were backgrounded.
			if (
				!current ||
				!sameAccessToken(current, stored) ||
				accessFingerprint(current.access) !== accessFingerprint(stored.access)
			) {
				commitSession(stored, { persist: false, broadcast: false, reason: 'focus-hydrate' });
			}

			// Near-expiry slides are handled by installSessionKeepAlive (focus + visibility).
			void requestPermissionsViaRest();
			requestPermissionsViaSocket();
		};

		const onVisibility = () => {
			if (document.visibilityState === 'visible') onFocus();
		};

		window.addEventListener('focus', onFocus);
		document.addEventListener('visibilitychange', onVisibility);
		return () => {
			window.removeEventListener('focus', onFocus);
			document.removeEventListener('visibilitychange', onVisibility);
		};
	}, [
		commitSession,
		navigate,
		requestPermissionsViaRest,
		requestPermissionsViaSocket,
	]);

	const access: AccessPolicy = useMemo(() => {
		void accessVersion;
		return session?.access
			? normalizeAccess(session.access.resources, session.access.postings)
			: EMPTY_ACCESS;
	}, [session?.access, accessVersion]);

	const value: Props = useMemo(
		() => ({
			session,
			accessVersion,
			access,
			setSession,
			logout,
			checkSession,
			requestSessionKeepAlive,
			sessionRef,
			api,
		}),
		[
			session,
			accessVersion,
			access,
			setSession,
			logout,
			checkSession,
			requestSessionKeepAlive,
		]
	);

	return (
		<AuthContext.Provider value={value}>
			{children}
		</AuthContext.Provider>
	);
};

export { AuthProvider, AuthContext };
